import {ChangeDetectionStrategy, Component, DestroyRef, signal} from '@angular/core'
import {OptableLayout} from '../../../../core/components/layout/optable-layout.interface'
import {map} from 'rxjs/operators'
import {PageComponent, PageMetadata} from '../../../../core/seo/page-metadata'
import {Observable, of, zip} from 'rxjs'
import {LinkService} from '../../../../core/i18n/link.service'
import {PUBLIC_ROUTES} from '../../../../shared/routing/public.routes'
import {TranslocoService} from '@ngneat/transloco'
import {CraftsmenRegionPagesContentService} from '../../../../core/content/services/craftsmen-region-pages-content.service'
import {
  CraftsmenRegionDirectoryPageContent,
  Region,
  RegionalIndustry,
  RegionWithExcludedIndustries,
  SideNavigationIndustryItem,
} from '../../../../core/content/types/craftsmen-region-types'
import {Language} from '../../../../core/i18n/language'
import {takeUntilDestroyed} from '@angular/core/rxjs-interop'

const regionPageSize = 100

@Component({
  selector: 'bgo-craftsmen-region-directory-page',
  templateUrl: './region-directory-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RegionDirectoryPageComponent extends PageComponent implements OptableLayout {
  optOutOfLayout = true

  protected showStickyFooter = true

  protected regions = signal<{
    page: {offset: number; size: number; total: number}
    value: RegionWithExcludedIndustries[]
  }>({
    page: {offset: 0, size: regionPageSize, total: regionPageSize},
    value: [],
  })
  protected content$!: Observable<CraftsmenRegionDirectoryPageContent[]>
  protected industries$!: Observable<RegionalIndustry[]>
  protected topRegions$!: Observable<Region[]>
  protected sideNavigationIndustries$!: Observable<SideNavigationIndustryItem[]>

  constructor(
    private readonly linkService: LinkService,
    private readonly translationService: TranslocoService,
    private readonly contentService: CraftsmenRegionPagesContentService,
    private readonly destroyRef: DestroyRef,
  ) {
    super()
    const page = this.contentService.getCraftsmenRegionDirectoryPage(regionPageSize)
    page.regions$.pipe(takeUntilDestroyed()).subscribe(r => {
      const regions = this.regions()
      this.regions.set({
        value: r.value,
        page: {...regions.page, total: r.total},
      })
    })
    this.topRegions$ = page.topRegions$
    this.industries$ = page.industries$
    this.content$ = page.content$
    this.sideNavigationIndustries$ = page.sideNavigationIndustries$
  }

  getPageMetadata(): Observable<PageMetadata> {
    return zip([
      this.translationService.selectTranslate('regionsDirectoryPage.title'),
      this.translationService.selectTranslate('regionsDirectoryPage.subtitle'),
    ]).pipe(map(([title, description]) => ({title, description})))
  }

  getPageAvailableLanguages(): Observable<Language[]> {
    return of(Object.values(Language))
  }

  navigateToAboutUsPage(): Promise<boolean> {
    return this.linkService.navigateTo(PUBLIC_ROUTES.ABOUT_US)
  }

  regionPageOffsetChanged(offset: number) {
    this.regions.update(r => ({...r, page: {...r.page, offset}}))
    this.contentService
      .getCraftsmenRegionDirectoryRegions({offset, size: regionPageSize})
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(r => {
        this.regions.set({
          value: r.value,
          page: {size: regionPageSize, offset, total: r.total},
        })
      })
  }
}
